import { LucideIcon } from 'lucide-react';
import { MonthStatus } from '@/types/payroll-periods';

// Payment stages for the Kanban board
export type PaymentStageId = 'upcoming' | 'ready' | 'processing' | 'completed' | 'failed';

export interface PaymentStage {
  id: PaymentStageId;
  label: string;
  color: 'blue' | 'orange' | 'yellow' | 'green' | 'red';
  icon: LucideIcon;
}

// Payment type discriminator
export type PaymentType = 'employee' | 'institutional';

// Individual payment item in a column
export interface PaymentItem {
  id: string;
  paymentType: PaymentType;
  amount: number; // Net pay amount
  currency: string;
  stage: PaymentStageId;

  // Employee payment fields
  employeeId?: string;
  employeeName?: string;
  grossAmount?: number;
  hoursWorked?: number;
  payrollId?: string;
  payrollStatus?: string;

  // Institutional payment fields (SK only)
  institutionName?: string;
  dueDate?: Date;

  // Stripe payment fields
  stripePaymentId?: string;
  stripeTransferId?: string;
  failureReason?: string;
  failureCode?: string;
  scheduledAt?: Date;
  processedAt?: Date;
  arrivedAt?: Date;
  paymentStatus?: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled';
}

// Stage counts and totals for headers
export interface StageStats {
  count: number;
  totalAmount: number;
}

// Selection state
export interface PaymentSelection {
  selectedIds: Set<string>;
  selectAll: (stage: PaymentStageId) => void;
  deselectAll: () => void;
  toggle: (id: string) => void;
  isSelected: (id: string) => boolean;
}

// Payment availability based on month status
export interface PaymentAvailability {
  qrCodesAvailable: boolean;
  qrCodesBulkAvailable: boolean;
  stripePaymentsAvailable: boolean;
  readOnlyMode: boolean;
  showPaymentWarning: boolean;
  paymentWarningMessage?: string;
  userFriendlyState: 'FUTURE' | 'UPCOMING' | 'IN_PROGRESS' | 'TO_BE_PROCESSED' | 'PROCESSED';
}

// Notification for the notification area
export interface PaymentNotification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  dismissible?: boolean;
}

// Props for main hub component
export interface UnifiedPaymentHubProps {
  businessId: string;
  businessCountryCode?: string;
  selectedMonth: Date;
  payrollRecords: PayrollRecord[];
  currentMonthStatus?: MonthStatus | null;
  onMonthChange?: (date: Date) => void;
}

// Payroll record from parent
export interface PayrollRecord {
  id: string;
  employee_id: string;
  employee_name: string;
  actual_hours: number;
  gross_pay: number;
  net_pay: number;
  calculation_method: string;
  country_code: string | null;
  auto_calculated: boolean;
  created_at: string;
  status: string;
}

// Color config for stages - using muted backgrounds for theme compatibility
export const STAGE_COLORS: Record<PaymentStage['color'], {
  header: string;
  border: string;
  icon: string;
}> = {
  blue: {
    header: 'bg-muted',
    border: 'border-border',
    icon: 'text-blue-600',
  },
  orange: {
    header: 'bg-muted',
    border: 'border-border',
    icon: 'text-orange-600',
  },
  yellow: {
    header: 'bg-muted',
    border: 'border-border',
    icon: 'text-yellow-600',
  },
  green: {
    header: 'bg-muted',
    border: 'border-border',
    icon: 'text-green-600',
  },
  red: {
    header: 'bg-muted',
    border: 'border-border',
    icon: 'text-red-600',
  },
};

// Utility function to map MonthStatus to PaymentStageId
export function getStageFromMonthStatus(
  monthStatus: MonthStatus | null | undefined,
  payrollRecordsExist: boolean
): PaymentStageId {
  if (!monthStatus) {
    return payrollRecordsExist ? 'ready' : 'upcoming';
  }

  switch (monthStatus.type) {
    case 'future':
    case 'future_planning':
    case 'current_planning':
      return 'upcoming';
    case 'current_incomplete':
    case 'last_complete':
      return 'ready';
    case 'historical':
    case 'locked':
      return 'completed';
    default:
      return 'ready';
  }
}

// Utility function to map payment status to stage
export function getStageFromPaymentStatus(
  status: PaymentItem['paymentStatus'] | undefined
): PaymentStageId {
  if (!status) return 'ready';

  switch (status) {
    case 'pending':
      return 'ready';
    case 'processing':
      return 'processing';
    case 'succeeded':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'canceled':
      return 'completed';
    default:
      return 'ready';
  }
}
